import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, MapPin, Plus, Search, ShieldCheck, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { useAuth } from "@/context/AuthContext";
import { request } from "@/lib/api";
import { getEarliestBookingDateIso, getLatestBookingDateIso, getEarliestHomeCollectionDateIso, getHomeCollectionDateOptions, appointmentSlots, formatBookingDate, slotSeparator } from "@/lib/appointments";

const defaultForm = {
  city: "",
  booking_type: "hybrid",
  pathology_provider: "",
  home_address: "",
  home_place_id: null,
  home_latitude: null,
  home_longitude: null,
  medical_center_id: "",
  appointment_date: getEarliestBookingDateIso(),
  appointment_slot: appointmentSlots[0],
  home_collection_date: getEarliestHomeCollectionDateIso(),
  home_collection_slot: appointmentSlots[0],
};

const emptyDraft = {
  beneficiary: "self",
  family_member_id: null,
  beneficiary_name: "",
  relationship: "Self",
  beneficiary_dob: "",
  beneficiary_mobile: "",
  beneficiary_gender: "",
  package_id: "",
};

let nextLocalId = 1;

export function BookingWizard({ onComplete }) {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [catalog, setCatalog] = useState({ packages: [], providers: [], centers: [] });
  const [savedFamily, setSavedFamily] = useState([]);
  // The employee's own details, so booking for yourself doesn't mean retyping
  // what we already hold. Anything they change or add is saved back on booking.
  const [employeeProfile, setEmployeeProfile] = useState(null);
  // Whether the employee's own check is still theirs to book, already booked,
  // or already used — which decides whether they can add themselves at all,
  // and whether it costs them anything.
  const [selfEligibility, setSelfEligibility] = useState(null);
  // The state of whoever the draft is currently for. For the employee this is
  // just selfEligibility; for a covered spouse it depends on which spouse, so
  // it is re-read once a name is known.
  const [draftEligibility, setDraftEligibility] = useState(null);
  // What the chosen centre actually has free. The fixed grid below is only
  // used where nothing downstream can contradict it.
  const [centreSlots, setCentreSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [centerSearch, setCenterSearch] = useState("");

  useEffect(() => {
    // Eligibility is the only one of these the wizard can do without: if it
    // can't be read the form still works, it just doesn't warn anyone up front
    // and the server has the last word. Letting it reject would take the whole
    // catalogue down with it.
    Promise.all([request("/catalog/packages", {}, token), request("/catalog/pathology-providers"), request("/catalog/centers"), request("/family-members", {}, token), request("/portal/profile", {}, token), request("/bookings/eligibility", {}, token).catch(() => null)])
      .then(([packages, providers, centers, family, profile, eligibility]) => {
        setEmployeeProfile(profile);
        setSelfEligibility(eligibility);
        setCatalog({ packages, providers, centers });
        setSavedFamily(family);
      })
      .catch(() => setError("We could not load the health-check catalogue."));
  }, [token]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updateDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  // Both schedules default to the earliest date and first slot, which for a
  // hybrid booking would put the employee at home and at the centre in the
  // same half hour. Give the centre visit the following day instead.
  useEffect(() => {
    if (!form.medical_center_id || !form.appointment_date) {
      setCentreSlots(null);
      return undefined;
    }
    let cancelled = false;
    setSlotsLoading(true);
    request(`/catalog/centers/${form.medical_center_id}/slots?date=${form.appointment_date}`, {}, token)
      .then((result) => {
        if (cancelled) return;
        setCentreSlots(result);
        // Drop a selection the centre doesn't actually offer, rather than
        // letting it through to fail at Apollo after the booking is made.
        const offered = (result.slots || []).map((item) => item.slot);
        if (offered.length && !offered.includes(form.appointment_slot)) {
          update("appointment_slot", offered[0]);
        }
      })
      .catch(() => !cancelled && setCentreSlots(null))
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.medical_center_id, form.appointment_date, token]);

  // Slots the centre genuinely has, else the fixed grid.
  const liveSlots = centreSlots?.source === "provider" ? (centreSlots.slots || []).map((item) => item.slot) : null;
  const liveSlotsEmpty = centreSlots?.source === "provider" && (centreSlots.slots || []).length === 0;

  // The home collection runs on our own working days. There is no availability
  // to ask anyone for — the round is arranged with the pathology partner
  // offline — so the list is ours, and weekends simply aren't in it.
  const homeCollectionDates = useMemo(() => getHomeCollectionDateOptions(), []);

  // Apollo publishes the dates an agreement can actually be booked on, and its
  // horizon is shorter than our 30-day window. Offering the rest is what made
  // bookings fail after the fact.
  const packageIds = useMemo(
    () => [...new Set(beneficiaries.map((item) => item.package_id).filter(Boolean))].sort().join(","),
    [beneficiaries],
  );
  const [bookableDates, setBookableDates] = useState(null);

  useEffect(() => {
    const ids = packageIds ? packageIds.split(",") : [];
    if (!ids.length) {
      setBookableDates(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        request(`/catalog/bookable-dates?package_id=${encodeURIComponent(id)}`, {}, token).catch(() => ({ dates: null })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const lists = results.map((result) => result?.dates).filter(Array.isArray);
      // Everyone on this booking attends together, so only dates every one of
      // their packages can be booked on are offered.
      setBookableDates(lists.length ? lists.reduce((shared, list) => shared.filter((day) => list.includes(day))) : null);
    });
    return () => {
      cancelled = true;
    };
  }, [packageIds, token]);

  // Hybrid is always offered. Apollo marks these packages as in-clinic, and
  // for a while that hid the option — but Apollo only ever handles the centre
  // half. The home collection is our own round, arranged with the pathology
  // partner offline, so whether Apollo's own package includes one says nothing
  // about whether we can collect at home.

  // The two halves can share a day in either order, but not a slot: that would
  // put the same person at home and at the centre at the same time. Rather
  // than let it be picked and fail at submit, the clashing slot is removed
  // from whichever list is being chosen from.
  const sameDay = form.booking_type === "hybrid" && form.appointment_date === form.home_collection_date;
  const slotKey = (slot) => (slot || "").replace("–", "-").replace(/\s/g, "");

  const visibleSlots = useMemo(() => {
    const base = liveSlots ?? appointmentSlots;
    if (!sameDay) return base;
    return base.filter((slot) => slotKey(slot) !== slotKey(form.home_collection_slot));
  }, [liveSlots, sameDay, form.home_collection_slot]);

  const collectionSlotOptions = useMemo(() => {
    if (!sameDay) return appointmentSlots;
    return appointmentSlots.filter((slot) => slotKey(slot) !== slotKey(form.appointment_slot));
  }, [sameDay, form.appointment_slot]);

  // Keep the selections inside what's offered: changing one side can
  // invalidate the other's choice a moment later.
  useEffect(() => {
    setForm((current) => {
      const next = { ...current };
      if (bookableDates?.length && !bookableDates.includes(next.appointment_date)) {
        next.appointment_date = bookableDates[0];
      }
      if (current.booking_type === "hybrid") {
        if (!homeCollectionDates.includes(next.home_collection_date)) {
          next.home_collection_date = homeCollectionDates[0] || next.home_collection_date;
        }
        if (
          next.home_collection_date === next.appointment_date
          && slotKey(next.home_collection_slot) === slotKey(next.appointment_slot)
        ) {
          // Move the collection rather than the centre visit: the centre slot
          // is the constrained one, the collection round is ours to shuffle.
          const free = appointmentSlots.find((slot) => slotKey(slot) !== slotKey(next.appointment_slot));
          if (free) next.home_collection_slot = free;
        }
      }
      const changed = Object.keys(next).some((key) => next[key] !== current[key]);
      return changed ? next : current;
    });
  }, [bookableDates, homeCollectionDates, form.booking_type, form.appointment_date, form.appointment_slot, form.home_collection_date, form.home_collection_slot]);

  const chooseHybrid = () => setForm((current) => ({ ...current, booking_type: "hybrid" }));
  const hasSelf = beneficiaries.some((item) => item.beneficiary === "self");
  const selfBlocked = selfEligibility ? !selfEligibility.can_book : false;
  const selfIsPayable = Boolean(selfEligibility?.self_pay);
  // Who this company pays for. Defaults to the employee alone, which is what
  // every client had before cover became a term of the agreement.
  const sponsoredFor = employeeProfile?.sponsored_relationships || ["self"];
  const isSponsored = (beneficiary) => sponsoredFor.includes(beneficiary);
  // Two different questions that used to have one answer. Anyone who is not
  // the employee needs their own name, date of birth and mobile — whether or
  // not the company is paying — and a covered spouse pays nothing while still
  // needing all of it.
  const draftIsFamily = draft.beneficiary !== "self";
  // A repeat check, booked before the interval is up, is the employee's own to
  // pay for — so it needs a priced package like any other.
  const draftNeedsPayment = draft.beneficiary === "self"
    ? selfIsPayable
    : (!isSponsored(draft.beneficiary) || Boolean(draftEligibility?.self_pay));
  const draftValidMobile = /^[6-9]\d{9}$/.test(draft.beneficiary_mobile);
  const draftPackages = catalog.packages.filter(
    (item) =>
      (!draftNeedsPayment || item.price > 0) &&
      (!draft.beneficiary_gender || !item.applicable_genders?.length || item.applicable_genders.includes(draft.beneficiary_gender))
  );
  useEffect(() => {
    if (!draft.beneficiary_gender || draftPackages.some((item) => item.id === draft.package_id)) return;
    setDraft((current) => ({ ...current, package_id: draftPackages[0]?.id || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.beneficiary_gender, draftNeedsPayment, catalog.packages]);

  // A covered spouse is subject to the same interval as the employee, and
  // which spouse decides the answer — so this is re-read once there is a name
  // to ask about. Failure is silent for the same reason as the first read:
  // the server has the last word and the form still works without the warning.
  useEffect(() => {
    if (draft.beneficiary === "self" || !isSponsored(draft.beneficiary)) {
      setDraftEligibility(null);
      return;
    }
    const name = draft.beneficiary_name.trim();
    if (!name) {
      setDraftEligibility(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ beneficiary: draft.beneficiary, name });
    if (draft.beneficiary_dob) params.set("dob", draft.beneficiary_dob);
    const timer = setTimeout(() => {
      request(`/bookings/eligibility?${params.toString()}`, {}, token)
        .then((state) => { if (!cancelled) setDraftEligibility(state); })
        .catch(() => { if (!cancelled) setDraftEligibility(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, draft.beneficiary, draft.beneficiary_name, draft.beneficiary_dob, employeeProfile]);

  const activeEligibility = draft.beneficiary === "self" ? selfEligibility : draftEligibility;

  const availableCities = [...new Set(catalog.centers.map((center) => center.city))].sort((left, right) => left.localeCompare(right));
  const cityCenters = catalog.centers.filter((center) => center.city === form.city);
  const centerSearchNeedle = centerSearch.trim().toLowerCase();
  const matchingCenters = centerSearchNeedle
    ? cityCenters.filter((center) => `${center.name} ${center.area} ${center.address}`.toLowerCase().includes(centerSearchNeedle))
    : cityCenters;
  const CENTER_DISPLAY_LIMIT = 30;
  const visibleCenters = matchingCenters.slice(0, CENTER_DISPLAY_LIMIT);
  const hiddenCenterCount = matchingCenters.length - visibleCenters.length;
  const totalAmount = beneficiaries.reduce((sum, item) => {
    const pkg = catalog.packages.find((entry) => entry.id === item.package_id);
    const covered = item.covered;
    return sum + (covered ? 0 : pkg?.price || 0);
  }, 0);

  function formatDate(value) {
    if (!value) return "—";
    return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" }).format(new Date(`${value}T00:00:00`));
  }

  function selectBeneficiaryType(type) {
    const relationship = { self: "Self", spouse: "Spouse", parent: "Parent", dependent: "Other dependent" }[type];
    if (type === "self") {
      // Prefill from the employee record. Every field stays editable — this is
      // a starting point, not a lock — and whatever they correct is saved back.
      setDraft((current) => ({
        ...emptyDraft,
        beneficiary: type,
        relationship,
        beneficiary_name: employeeProfile?.name || "",
        beneficiary_gender: employeeProfile?.gender || current.beneficiary_gender || "",
        beneficiary_dob: employeeProfile?.date_of_birth || "",
        beneficiary_mobile: employeeProfile?.mobile || "",
      }));
      return;
    }
    setDraft((current) => ({ ...emptyDraft, beneficiary: type, relationship, beneficiary_gender: current.beneficiary_gender }));
  }

  function selectSavedFamily(member) {
    const beneficiary = member.relationship === "Spouse" ? "spouse" : member.relationship === "Parent" ? "parent" : "dependent";
    setDraft({
      beneficiary,
      family_member_id: member.family_member_id,
      beneficiary_name: member.name,
      relationship: member.relationship,
      beneficiary_dob: member.date_of_birth,
      beneficiary_mobile: member.mobile_number,
      beneficiary_gender: member.gender || "",
      package_id: "",
    });
    // Their saved address is the collection address for this booking.
    if (member.address) {
      setForm((current) => ({
        ...current,
        home_address: member.address,
        home_place_id: member.address_place_id || null,
        home_latitude: member.address_latitude || null,
        home_longitude: member.address_longitude || null,
      }));
    }
  }

  function canAddDraft() {
    if (!draft.beneficiary_name || !draft.beneficiary_gender || !draft.package_id) return false;
    if (draft.beneficiary === "self" && (hasSelf || selfBlocked)) return false;
    return !draftIsFamily || Boolean(draft.relationship && draft.beneficiary_dob && draftValidMobile);
  }

  function addBeneficiary() {
    if (!canAddDraft()) {
      setError("Fill in this person's details, gender, and package before adding them.");
      return;
    }
    setError("");
    // Whether this person is covered is settled as they are added, from the
    // eligibility the server gave for them. Deciding it later from the
    // beneficiary type alone would show a covered spouse a price.
    setBeneficiaries((current) => [
      ...current,
      { ...draft, covered: !draftNeedsPayment, localId: nextLocalId++, status: "pending", errorMessage: "" },
    ]);
    const nextType = draft.beneficiary === "self" || hasSelf ? "dependent" : "self";
    setDraft({ ...emptyDraft, beneficiary: nextType, relationship: nextType === "self" ? "Self" : "Other dependent" });
  }

  function removeBeneficiary(localId) {
    setBeneficiaries((current) => current.filter((item) => item.localId !== localId));
  }

  function canContinue() {
    if (step === 1) return beneficiaries.length > 0;
    if (step === 2) return form.booking_type === "clinic" || Boolean(form.pathology_provider && form.home_address && form.home_collection_date && form.home_collection_slot);
    if (step === 3) return Boolean(form.city && form.medical_center_id && form.appointment_date && form.appointment_slot && !liveSlotsEmpty && bookableDates?.length !== 0);
    return true;
  }

  async function submit() {
    setError("");
    setIsSaving(true);
    const pending = beneficiaries.filter((item) => item.status !== "success");
    let working = beneficiaries;
    for (const item of pending) {
      const payload = {
        ...form,
        beneficiary: item.beneficiary,
        beneficiary_name: item.beneficiary_name,
        relationship: item.relationship,
        beneficiary_dob: item.beneficiary_dob || null,
        beneficiary_mobile: item.beneficiary_mobile || null,
        beneficiary_gender: item.beneficiary_gender,
        package_id: item.package_id,
        // Links back to the saved profile this came from, so the booking
        // refreshes it rather than saving a near-duplicate alongside it.
        family_member_id: item.family_member_id || null,
      };
      try {
        const booking = await request("/bookings", { method: "POST", body: JSON.stringify(payload) }, token);
        working = working.map((entry) => (entry.localId === item.localId ? { ...entry, status: "success", booking, errorMessage: "" } : entry));
      } catch (submitError) {
        working = working.map((entry) => (entry.localId === item.localId ? { ...entry, status: "error", errorMessage: submitError.message } : entry));
      }
      setBeneficiaries(working);
    }
    setIsSaving(false);
    if (working.every((entry) => entry.status === "success")) {
      onComplete(working.map((entry) => entry.booking));
    } else {
      setError("Some bookings could not be completed. Fix and retry the ones marked below.");
    }
  }

  return (
    <section className="booking-wizard" data-testid="health-check-booking-wizard">
      <div className="booking-progress" data-testid="booking-progress-indicator">
        {[1, 2, 3, 4].map((item) => <span key={item} className={item <= step ? "is-active" : ""}>{item}</span>)}
      </div>
      {step === 1 && <section className="wizard-section" data-testid="booking-beneficiary-step">
        <p className="eyebrow">Step 1 · Who is this for?</p><h2>Add a member for this trip</h2>
        {/* Red is reserved for the sponsorship interval, where the employee is
            about to pay for something they may expect to be covered. Having a
            check already booked costs them nothing and only points them at
            their appointments, so it stays a plain hint. */}
        {activeEligibility?.message && <p className={activeEligibility.reason === "within_interval" ? "package-loss-warning" : "step-hint"} data-testid="self-eligibility-note">{activeEligibility.message}</p>}
        <div className="choice-grid">{["self", "spouse", "parent", "dependent"].map((type) => {
          // "Covered" on a sponsored relationship is the headline claim, but
          // the employee's own cover can be used up, so that one follows their
          // eligibility rather than the policy alone.
          const label = type === "self" ? (selfIsPayable ? "Self-pay" : "Covered") : (isSponsored(type) ? "Covered" : "Self-pay");
          return <button key={type} disabled={type === "self" && (hasSelf || selfBlocked)} className={`choice-button ${draft.beneficiary === type ? "is-selected" : ""}`} type="button" onClick={() => selectBeneficiaryType(type)} data-testid={`beneficiary-${type}-button`}>{label}<strong>{type === "self" ? "Employee" : type[0].toUpperCase() + type.slice(1)}</strong></button>;
        })}</div>
        {draftIsFamily && savedFamily.length > 0 && <div className="saved-family-picker" data-testid="saved-family-picker"><p className="eyebrow">Use a saved profile</p>{savedFamily.filter((member) => !beneficiaries.some((item) => item.family_member_id === member.family_member_id)).map((member) => <button key={member.family_member_id} type="button" onClick={() => selectSavedFamily(member)} data-testid={`select-family-${member.family_member_id}-button`}><strong>{member.name}</strong><span>{member.relationship}</span></button>)}</div>}
        <label htmlFor="beneficiary-name">{draftIsFamily ? "Family member name" : "Employee name"}</label><input id="beneficiary-name" value={draft.beneficiary_name} onChange={(event) => updateDraft("beneficiary_name", event.target.value)} placeholder="Full name" data-testid="beneficiary-name-input" />
        <label htmlFor="beneficiary-gender">Gender</label><select id="beneficiary-gender" value={draft.beneficiary_gender} onChange={(event) => updateDraft("beneficiary_gender", event.target.value)} data-testid="beneficiary-gender-select"><option value="">Select gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select>
        {draft.beneficiary === "self" && <div className="form-stack"><label htmlFor="self-dob">Date of birth</label><input id="self-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={draft.beneficiary_dob} onChange={(event) => updateDraft("beneficiary_dob", event.target.value)} data-testid="self-dob-input" /><label htmlFor="self-mobile">Mobile number</label><input id="self-mobile" inputMode="tel" maxLength="10" value={draft.beneficiary_mobile} onChange={(event) => updateDraft("beneficiary_mobile", event.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile number" data-testid="self-mobile-input" /><small className="employee-muted" data-testid="self-details-hint">We'll remember these for your next booking. You can change them any time in your profile.</small></div>}
        {draftIsFamily && <div className="form-stack"><label htmlFor="family-relationship">Relationship</label><select id="family-relationship" value={draft.relationship} onChange={(event) => updateDraft("relationship", event.target.value)} data-testid="family-relationship-select"><option>Spouse</option><option>Parent</option><option>Child (18+)</option><option>Other dependent</option></select><label htmlFor="beneficiary-dob">Date of birth</label><input id="beneficiary-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={draft.beneficiary_dob} onChange={(event) => updateDraft("beneficiary_dob", event.target.value)} data-testid="dependent-dob-input" /><label htmlFor="beneficiary-mobile">Mobile number</label><input id="beneficiary-mobile" inputMode="tel" maxLength="10" value={draft.beneficiary_mobile} onChange={(event) => updateDraft("beneficiary_mobile", event.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile number" data-testid="dependent-mobile-input" /><small className="employee-muted" data-testid="dependent-details-hint">Saved as a profile so you can book for them again without retyping this.</small></div>}
        <div className="package-list">{draftPackages.map((item) => {
          const isExpanded = expandedPackageId === item.id;
          const hasMore = item.tests.length > 3;
          return <div key={item.id} className={`package-option ${draft.package_id === item.id ? "is-selected" : ""}`} data-testid={`package-${item.id}-button`}>
            <button type="button" className="package-option-summary" onClick={() => updateDraft("package_id", item.id)}>
              <span>
                <strong>{item.name}</strong>
                {/* Collapsed: a taste of what's in it plus the count, so the
                    package is recognisable without opening anything. */}
                <small>{item.tests.length ? `${item.tests.length} tests · ${item.tests.slice(0, 3).join(" · ")}${hasMore ? "…" : ""}` : "Test list coming soon"}</small>
              </span>
              <b>{draftNeedsPayment ? `₹${item.price.toLocaleString("en-IN")}` : "Covered"}</b>
            </button>
            {/* Expanded, the full list is one item per line rather than a
                run-on sentence — 27 test names joined by separators is
                technically complete and genuinely unreadable. */}
            {isExpanded && (
              <ul className="package-test-list" data-testid={`package-${item.id}-test-list`}>
                {item.tests.map((test) => <li key={test}>{test}</li>)}
              </ul>
            )}
            {hasMore && <button type="button" className="text-button package-details-toggle" onClick={() => setExpandedPackageId(isExpanded ? null : item.id)} data-testid={`package-${item.id}-details-toggle`}>{isExpanded ? "Hide full test list" : `View all ${item.tests.length} tests`}</button>}
          </div>;
        })}{draft.beneficiary_gender && !draftPackages.length && <p className="empty-state" data-testid="empty-gender-packages">No packages are available for this gender yet.</p>}</div>
        <Button type="button" variant="outline" onClick={addBeneficiary} data-testid="add-beneficiary-button"><Plus size={18} />Add to this booking</Button>
        {beneficiaries.length > 0 && <div className="beneficiary-list beneficiary-list--added" data-testid="added-beneficiaries-list"><p className="eyebrow">Added to this booking ({beneficiaries.length})</p>{beneficiaries.map((item) => <div className="beneficiary-row" key={item.localId} data-testid={`added-beneficiary-${item.localId}`}><span><strong>{item.beneficiary_name}</strong><small>{item.relationship} · {catalog.packages.find((p) => p.id === item.package_id)?.name || "—"}</small></span><button type="button" className="icon-button icon-button--outlined" onClick={() => removeBeneficiary(item.localId)} aria-label={`Remove ${item.beneficiary_name}`} data-testid={`remove-beneficiary-${item.localId}-button`}><Trash2 size={16} /></button></div>)}</div>}
      </section>}
      {step === 2 && <section className="wizard-section" data-testid="booking-pathology-step">
        <p className="eyebrow">Step 2 · Test location</p><h2>Choose the check-up format</h2>
        <div className="format-options"><button className={`format-option ${form.booking_type === "hybrid" ? "is-selected" : ""}`} type="button" onClick={() => chooseHybrid()} data-testid="hybrid-booking-type-button"><strong>Hybrid</strong><span>Pathology at the convenience of your home & radiology at the medical center</span></button><button className={`format-option ${form.booking_type === "clinic" ? "is-selected" : ""}`} type="button" onClick={() => update("booking_type", "clinic")} data-testid="clinic-booking-type-button"><strong>All in clinic</strong><span>Both Pathology & Radiology at the Medical Center</span></button></div>
        {form.booking_type === "hybrid" && <div className="form-stack"><label htmlFor="pathology-provider">Home collection provider</label><select id="pathology-provider" value={form.pathology_provider} onChange={(event) => update("pathology_provider", event.target.value)} data-testid="pathology-provider-select"><option value="">Choose a provider</option>{catalog.providers.map((provider) => <option value={provider.id} key={provider.id}>{provider.name}</option>)}</select><p className="eyebrow">Collection address</p><AddressAutocomplete id="home-address" token={token} value={form.home_address} onChange={(value) => update("home_address", value)} onAddressSelect={(place) => setForm((current) => ({ ...current, home_place_id: place?.placeId || null, home_latitude: place?.latitude || null, home_longitude: place?.longitude || null }))} placeholder="Search or enter the area" testId="home-address-input" />
          <p className="eyebrow">When should they collect?</p>
          <p className="step-hint" data-testid="home-collection-hint">Collections run Monday to Friday. Your centre visit can be the same day or another one — just not at the same time.</p>
          <label htmlFor="home-collection-date">Home collection date</label>
          <select id="home-collection-date" value={form.home_collection_date} onChange={(event) => update("home_collection_date", event.target.value)} data-testid="home-collection-date-select">{homeCollectionDates.map((day) => <option value={day} key={day}>{formatBookingDate(day)}</option>)}</select>
          <label htmlFor="home-collection-slot">Home collection slot</label>
          <select id="home-collection-slot" value={form.home_collection_slot} onChange={(event) => update("home_collection_slot", event.target.value)} data-testid="home-collection-slot-select">{collectionSlotOptions.map((slot) => <option key={slot}>{slot}</option>)}</select>
          {sameDay && <p className="step-hint" data-testid="same-day-collection-hint">Your centre visit is on this day at {form.appointment_slot}, so that slot isn't offered here.</p>}
        </div>}
      </section>}
      {step === 3 && <section className="wizard-section" data-testid="booking-radiology-step">
        <p className="eyebrow">Step 3 · Medical centre</p><h2>{form.booking_type === "hybrid" ? "Choose your appointment" : "Choose your clinic"}</h2>
        {form.booking_type === "hybrid" && <p className="step-hint" data-testid="hybrid-schedule-hint">This is your radiology visit at the centre. Your home sample collection is booked for {formatBookingDate(form.home_collection_date)}, {form.home_collection_slot}.</p>}
        <label htmlFor="medical-city">Choose city</label><select id="medical-city" value={form.city} onChange={(event) => { update("city", event.target.value); update("medical_center_id", ""); setCenterSearch(""); }} data-testid="medical-city-select"><option value="">Select city</option>{availableCities.map((city) => <option value={city} key={city}>{city}</option>)}</select>
        {form.city && cityCenters.length > 6 && <div className="center-search"><Search size={17} /><input type="search" value={centerSearch} onChange={(event) => setCenterSearch(event.target.value)} placeholder={`Search ${cityCenters.length} centres in ${form.city}`} aria-label="Search medical centres" data-testid="center-search-input" /></div>}
        {form.city && <label htmlFor="medical-center">Medical centre</label>}
        {form.city && <select id="medical-center" value={form.medical_center_id} onChange={(event) => update("medical_center_id", event.target.value)} disabled={!matchingCenters.length} data-testid="medical-center-select"><option value="">{matchingCenters.length ? "Select a medical centre" : "No centres match"}</option>{visibleCenters.map((center) => <option value={center.id} key={center.id}>{center.area || center.name} · {center.address}</option>)}</select>}
        {form.city && !matchingCenters.length && <p className="empty-state" data-testid="empty-city-centers">{centerSearchNeedle ? "No centres match your search." : "No medical centres are available for this city yet."}</p>}
        {hiddenCenterCount > 0 && <p className="step-hint" data-testid="center-search-truncated-hint">+{hiddenCenterCount} more — narrow your search to see them.</p>}
        {form.medical_center_id && <div className="selected-center-card" data-testid="selected-center-summary"><MapPin size={18} /><span><strong>{catalog.centers.find((c) => c.id === form.medical_center_id)?.name}</strong><small>{catalog.centers.find((c) => c.id === form.medical_center_id)?.address}</small></span></div>}
        <label htmlFor="appointment-date">{form.booking_type === "hybrid" ? "Centre visit date (radiology)" : "Appointment date"}</label>
        {/* Where the provider publishes the dates it will accept, those are
            the only ones offered — a free date field let people pick days that
            were refused once the booking reached them. */}
        {bookableDates?.length
          ? <select id="appointment-date" value={form.appointment_date} onChange={(event) => update("appointment_date", event.target.value)} data-testid="appointment-date-select">{bookableDates.map((day) => <option value={day} key={day}>{formatBookingDate(day)}</option>)}</select>
          : <input id="appointment-date" type="date" min={getEarliestBookingDateIso()} max={getLatestBookingDateIso()} value={form.appointment_date} onChange={(event) => update("appointment_date", event.target.value)} data-testid="appointment-date-input" />}
        {bookableDates?.length === 0 && <p className="form-error" role="alert" data-testid="no-bookable-dates">This package has no dates open with the provider at the moment. Please contact services@ivoryhealth.in.</p>}
        <label htmlFor="appointment-slot">{form.booking_type === "hybrid" ? "Centre visit slot" : "Available appointment slot"}</label>
        {slotsLoading && <p className="step-hint" data-testid="slots-loading">Checking what this centre has free…</p>}
        <select id="appointment-slot" value={form.appointment_slot} onChange={(event) => update("appointment_slot", event.target.value)} disabled={slotsLoading || liveSlotsEmpty} data-testid="appointment-slot-select">{visibleSlots.map((slot) => <option key={slot}>{slot}</option>)}</select>
        {liveSlotsEmpty && <p className="form-error" role="alert" data-testid="no-slots-message">{centreSlots?.message}</p>}
        {centreSlots?.source === "provider" && !liveSlotsEmpty && <p className="step-hint" data-testid="live-slots-hint">These are the centre's live openings for {formatBookingDate(form.appointment_date)}.</p>}
        {centreSlots?.message && centreSlots?.source === "generic" && <p className="step-hint" data-testid="indicative-slots-hint">{centreSlots.message}</p>}
        {sameDay && <p className="step-hint" data-testid="centre-slot-limited-hint">Your home collection is at {form.home_collection_slot} that day, so that slot isn't offered here.</p>}
      </section>}
      {step === 4 && <section className="wizard-section review-section" data-testid="booking-review-step"><p className="eyebrow">Step 4 · Review</p><h2>Ready when you are</h2>
        {beneficiaries.map((item) => {
          const pkg = catalog.packages.find((entry) => entry.id === item.package_id);
          return <div className="review-row review-row--beneficiary" key={item.localId} data-testid={`review-beneficiary-${item.localId}`}>
            <span>{item.beneficiary_name} <small>({item.relationship})</small></span>
            <strong>{pkg?.name || "—"} · {item.covered ? "Covered" : `₹${pkg?.price?.toLocaleString("en-IN") || "0"}`}</strong>
            {item.status === "success" && <span className="success-inline" data-testid={`beneficiary-status-${item.localId}`}><CheckCircle2 size={15} />Booked</span>}
            {item.status === "error" && <p className="form-error" role="alert" data-testid={`beneficiary-error-${item.localId}`}>{item.errorMessage}</p>}
          </div>;
        })}
        <div className="review-row"><span>Format</span><strong>{form.booking_type === "hybrid" ? "Hybrid check-up" : "All in clinic"}</strong></div>
        {/* A hybrid booking is two appointments; showing only one on the
            review screen is how people end up surprised by the other. */}
        {form.booking_type === "hybrid" && <div className="review-row" data-testid="review-home-collection"><span>Home collection</span><strong>{formatBookingDate(form.home_collection_date)} · {form.home_collection_slot}</strong></div>}
        <div className="review-row" data-testid="review-centre-visit"><span>{form.booking_type === "hybrid" ? "Centre visit (radiology)" : "Appointment"}</span><strong>{formatBookingDate(form.appointment_date)} · {form.appointment_slot}</strong></div>
        <div className="review-row"><span>Total payment</span><strong>{totalAmount > 0 ? `₹${totalAmount.toLocaleString("en-IN")}` : "Covered"}</strong></div>
        {totalAmount > 0 && <p className="payment-note" data-testid="payment-handoff-note"><ShieldCheck size={18} />Pay securely to book each self-pay health check.</p>}
      </section>}
      {error && <p className="form-error" role="alert" data-testid="booking-error-message">{error}</p>}
      <div className="wizard-actions">{step > 1 && <Button variant="outline" onClick={() => setStep((current) => current - 1)} data-testid="booking-back-button"><ChevronLeft size={18} />Back</Button>}{step < 4 ? <Button onClick={() => canContinue() ? setStep((current) => current + 1) : setError("Complete this step to continue.")} data-testid="booking-next-button">Continue<ChevronRight size={18} /></Button> : <Button disabled={isSaving} onClick={submit} data-testid="booking-submit-button">{isSaving ? "Saving…" : <><CheckCircle2 size={18} />{beneficiaries.some((item) => item.status === "error") ? "Retry failed bookings" : "Confirm request"}</>}</Button>}</div>
    </section>
  );
}
